/**
 * Return-from-auth funnel event (CHECKOUT_RETURNED_FROM_AUTH).
 *
 * After OTP on the `/aui` login page, Auth redirects the shopper back to PPUI.
 * The handshake redirect tagged that return URL with `from=aui` (see
 * ./build-auth-login-url). On landing we emit a New Relic custom event so the
 * funnel can measure who actually came back.
 *
 * WHY IT IS EMITTED HERE, BEFORE THE TOKEN CHECK
 * ----------------------------------------------
 * The whole value of this event is capturing the post-OTP → PPUI drop,
 * including the token-handoff failures where the checkout token never arrives
 * and the plan screen (PAYMENT_PLAN_SELECTION_SCREEN_VIEWED) never renders. So
 * it must fire independently of the session/token: it uses the keepalive beacon
 * (which needs no checkout token) and is called at app boot, not gated on a
 * successful session fetch.
 *
 * The landing page is long-lived, so the browser agent would deliver this fine
 * too — but reusing the beacon keeps every funnel event on one transport and
 * one custom-event type (CheckoutUiEvents).
 */

import { sendCheckoutUiBeacon } from "./nr-beacon";
import { getCheckoutToken } from "@/services/cookie";
import { RIVER_EVENT, trackRiver } from "@/services/river";
import {
  AUTH_RETURN_MARKER_PARAM,
  AUTH_RETURN_MARKER_VALUE,
} from "./build-auth-login-url";

/** Funnel event name. Keep in sync with the NeoCosmos funnel dashboard. */
export const CHECKOUT_RETURNED_FROM_AUTH_EVENT = "CHECKOUT_RETURNED_FROM_AUTH";

/** Per-checkout guard so a reload of the landing URL cannot double-count. */
const FIRED_STORAGE_PREFIX = "returned_from_auth_fired:";

function alreadyFired(checkoutId: string): boolean {
  try {
    return (
      sessionStorage.getItem(`${FIRED_STORAGE_PREFIX}${checkoutId}`) === "1"
    );
  } catch {
    return false;
  }
}

function markFired(checkoutId: string): void {
  try {
    sessionStorage.setItem(`${FIRED_STORAGE_PREFIX}${checkoutId}`, "1");
  } catch {
    // sessionStorage can be unavailable (private mode / sandboxed webview) —
    // the URL-marker strip below is the primary guard against re-firing.
  }
}

/**
 * Removes the `from=aui` marker from the URL (in place, no navigation) once the
 * landing has been processed. This is the primary dedupe guard — sessionStorage
 * can be unavailable in sandboxed webviews, but stripping the marker means a
 * reload simply has nothing to react to, so the event cannot re-fire.
 */
function clearReturnMarker(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(AUTH_RETURN_MARKER_PARAM) === null) return;
    url.searchParams.delete(AUTH_RETURN_MARKER_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
  } catch {
    // Best-effort; never break app boot.
  }
}

/**
 * Emits CHECKOUT_RETURNED_FROM_AUTH once per checkout when the current URL
 * carries the `from=aui` marker. No-op otherwise. Never throws.
 *
 * @param checkoutId resolved at app boot (from the URL/sessionStorage).
 */
export function reportReturnedFromAuth(checkoutId: string | undefined): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(AUTH_RETURN_MARKER_PARAM) !== AUTH_RETURN_MARKER_VALUE) {
      return;
    }
    // Need an id to dedupe and to join the event to the funnel. The Auth
    // redirect always carries checkoutId, so this should be present.
    if (checkoutId && !alreadyFired(checkoutId)) {
      sendCheckoutUiBeacon(CHECKOUT_RETURNED_FROM_AUTH_EVENT, {
        checkout_id: checkoutId,
        order_id: params.get("orderId") ?? "",
      });
      // Read here rather than taking it from the caller: this runs before
      // App's token gate, which is the point - a return with no token is the
      // handoff failure we most want counted, and it reports authenticated:
      // false rather than going missing.
      trackRiver({
        event: RIVER_EVENT.authReturned,
        checkoutId,
        payload: { authenticated: Boolean(getCheckoutToken()) },
        context: { orderId: params.get("orderId") ?? undefined },
      });
      markFired(checkoutId);
    }

    // Strip the marker once processed so a reload cannot re-fire, even where
    // sessionStorage is unavailable. Done after reading `params` above.
    clearReturnMarker();
  } catch {
    // Telemetry must never break app boot.
  }
}
